import React, { useState, useEffect, useCallback } from 'react';
import {
    LogOut,
    Users,
    BarChart3,
    Shield,
    AlertTriangle,
    CheckCircle,
    Clock,
    FileText,
    AlertCircle,
    Activity,
    RefreshCw,
    Bell,
    UserCog,
    Settings,
    XCircle,
    Mail,
    Bot,
    ChevronDown,
    ChevronUp,
    MapPin,
    Building2,
    Info
} from 'lucide-react';
import { apiClient, DashboardStats, DriverDue, RecentActivity } from '../services/api';
import { Routes, Route, useNavigate, useLocation, Navigate, useSearchParams } from 'react-router-dom';
import { AuthResponse } from '../services/api';
import Drivers from './Drivers';
import SystemHealth from './SystemHealth';
import EmailAnalytics from './EmailAnalytics';
import NotFoundPage from './ErrorPages/NotFoundPage';
import LicenceCheckModal from './Modals/LicenceCheckModal';
import UserManagement from './UserManagement';
import NotificationSettings from './NotificationSettings';
import NotificationLogs from './NotificationLogs';
import Reports from './Reports';
import NotificationBell from './NotificationBell';
import AutomationSettings from './AutomationSettings';
import BulkOperationModal from './BulkOperationModal';
import ConsentSendResultsModal from './Modals/ConsentSendResultsModal';
import Footer from './Footer';
import OrganisationBranding from './OrganisationBranding';
import SiteRiskWidget from './SitesWidget';
import SiteRiskPage from './SitesPage';

type User = AuthResponse['user'];

interface DashboardProps {
    user: User | null;
    onLogout: () => void;
}

const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3, path: '/dashboard' },
    { id: 'drivers', label: 'Drivers', icon: Users, path: '/drivers' },
    { id: 'sites', label: 'Sites', icon: MapPin, path: '/sites' },
    { id: 'reports', label: 'Reports', icon: FileText, path: '/reports' },
    { id: 'users', label: 'User Management', icon: UserCog, path: '/user-management' },
    { id: 'settings', label: 'Settings', icon: Settings, path: '/settings' },
];

const Dashboard: React.FC<DashboardProps> = ({ user, onLogout }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();

    const [stats, setStats] = useState<DashboardStats | null>(null);
    const [driversDue, setDriversDue] = useState<DriverDue[]>([]);
    const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
    const [automationSettings, setAutomationSettings] = useState<any>(null);
    const [isAutomationExpanded, setIsAutomationExpanded] = useState(false);
    const [showAutoCheckTooltip, setShowAutoCheckTooltip] = useState(false);

    const isDashboardPage = location.pathname === '/dashboard';
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const [selectedDriverForModal, setSelectedDriverForModal] = useState<any>(null);
    const [isLicenceCheckModalOpen, setIsLicenceCheckModalOpen] = useState(false);

    // Bulk operation modal state
    const [bulkOperationId, setBulkOperationId] = useState<string | null>(null);
    const [isBulkOperationModalOpen, setIsBulkOperationModalOpen] = useState(false);

    // Consent results modal state
    const [consentResultsData, setConsentResultsData] = useState<any>(null);
    const [showConsentResultsModal, setShowConsentResultsModal] = useState(false);

    const loadDashboardData = useCallback(async () => {
        try {
            setIsLoading(true);
            setError(null);

            const stats = await apiClient.getDashboardStats();
            const driversDue = await apiClient.getDriversDue();
            const recentActivity = await apiClient.getRecentActivity();

            setStats(stats);
            setDriversDue(driversDue);
            setRecentActivity(recentActivity);
            setLastRefresh(new Date());

            // Fetch automation settings if organisation has the feature
            if (user?.organisation?.automatedChecks) {
                try {
                    const settings = await apiClient.getAutomationSettings();
                    setAutomationSettings(settings);
                } catch (err) {
                    console.error('Failed to load automation settings:', err);
                }
            }
        } catch (err: any) {
            setError(err.message || 'Failed to load dashboard data');
            console.error('Dashboard error:', err);
        } finally {
            setIsLoading(false);
        }
    }, [user?.organisation?.automatedChecks]);

    useEffect(() => {
        if (isDashboardPage) {
            loadDashboardData();
        }
    }, [isDashboardPage, loadDashboardData]);

    // Listen for bulkOperationId in URL params and auto-open modal
    useEffect(() => {
        const bulkOpId = searchParams.get('bulkOperationId');
        if (bulkOpId && isDashboardPage) {
            setBulkOperationId(bulkOpId);
            setIsBulkOperationModalOpen(true);
            // Remove the param from URL after opening modal
            searchParams.delete('bulkOperationId');
            setSearchParams(searchParams, { replace: true });
        }
    }, [searchParams, isDashboardPage, setSearchParams]);

    if (!user) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="animate-spin h-8 w-8 border-4 border-t-transparent rounded-full" style={{ borderColor: '#69BE28 transparent transparent transparent' }}></div>
            </div>
        );
    }

    const handleLogout = () => {
        apiClient.logout();
        onLogout();
    };

    const getRiskBadgeColor = (riskLevel: 'low' | 'medium' | 'high' | null) => {
        switch (riskLevel) {
            case 'low': return 'bg-green-100 text-green-800';
            case 'medium': return 'bg-yellow-100 text-yellow-800';
            case 'high': return 'bg-red-100 text-red-800';
            case null:
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const formatTime = (dateString: string) => {
        return new Date(dateString).toLocaleTimeString('en-GB', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleViewDetails = async (activity: RecentActivity) => {
        try {
            const driversResponse = await apiClient.getDrivers({
                search: activity.licenceNumber,
                pageSize: 1
            });

            if (driversResponse.data.length === 0) {
                setError('Driver not found');
                return;
            }

            const driver = driversResponse.data[0];
            setSelectedDriverForModal(driver);
            setIsLicenceCheckModalOpen(true);
        } catch (error: any) {
            console.error('Failed to load driver details:', error);
            setError(error.message || 'Failed to load driver details');
        }
    };

    const ConsentRateCard = () => {
        if (!stats) {
            return (
                <div className="bg-gray-50 rounded-lg shadow p-6 border flex flex-col h-full">
                    <div className="flex items-center">
                        <div className="p-2 bg-gray-100 rounded-md">
                            <Shield className="w-6 h-6 text-gray-400" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Consent Rate</p>
                            <p className="text-2xl font-bold text-gray-400">...</p>
                        </div>
                    </div>
                    <div className="mt-auto pt-4">
                        <div className="pt-3 border-t border-gray-200">
                            <p className="text-sm text-gray-500">Loading...</p>
                        </div>
                    </div>
                </div>
            );
        }

        const { activeDrivers, driversWithConsent, driversWithoutConsent } = stats;

        if (activeDrivers === 0) {
            return (
                <div className="bg-gray-50 rounded-lg shadow p-6 border flex flex-col h-full">
                    <div className="flex items-center">
                        <div className="p-2 bg-gray-100 rounded-md">
                            <Users className="w-6 h-6 text-gray-400" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Consent Rate</p>
                            <p className="text-md font-bold text-gray-600">No drivers yet</p>
                        </div>
                    </div>
                    <div className="mt-auto pt-4">
                        <div className="pt-3 border-t border-gray-200">
                            <p className="text-sm text-gray-500">Add drivers first</p>
                        </div>
                    </div>
                </div>
            );
        }

        const consentRate = Math.round((driversWithConsent / activeDrivers) * 100);

        // Always use consistent white background, only change icon/text color
        const bgColorClass = "bg-white";
        let colorClass, icon;
        if (consentRate === 100) {
            colorClass = "#69BE28";
            icon = CheckCircle;
        } else if (consentRate >= 70) {
            colorClass = "#F59E0B"; // amber
            icon = AlertTriangle;
        } else {
            colorClass = "#EF4444"; // red
            icon = XCircle;
        }

        let subtitle = "";
        if (consentRate === 100) {
            subtitle = "All have D906 consent";
        } else {
            subtitle = `${driversWithConsent}/${activeDrivers} consented`;
            if (driversWithoutConsent > 0) {
                subtitle += ` • ${driversWithoutConsent} pending`;
            }
        }

        const ConsentIcon = icon;

        return (
            <div
                className={`${bgColorClass} rounded-lg shadow p-6 border flex flex-col h-full cursor-pointer hover:shadow-md transition-shadow`}
                onClick={() => navigate('/drivers?show=no_consent')}
            >
                <div className="flex items-center">
                    <div className="p-2 rounded-md" style={{ backgroundColor: 'rgba(105, 190, 40, 0.1)' }}>
                        <ConsentIcon className="w-6 h-6" style={{ color: colorClass }} />
                    </div>
                    <div className="ml-4">
                        <p className="text-sm font-medium text-gray-600">Consent Rate</p>
                        <p className="text-2xl font-bold" style={{ color: colorClass }}>
                            {consentRate}%
                        </p>
                    </div>
                </div>
                <div className="mt-auto pt-4">
                    <div className="pt-3 border-t border-gray-200">
                        <p className="text-sm text-gray-500 line-clamp-2">{subtitle}</p>
                    </div>
                </div>
            </div>
        );
    };

    const ClearToDriveCard = () => {
        if (!stats) {
            return (
                <div className="bg-white rounded-lg shadow p-6 border flex flex-col h-full">
                    <div className="flex items-center">
                        <div className="p-2 bg-gray-100 rounded-md">
                            <Shield className="w-6 h-6 text-gray-400" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Clear to Drive</p>
                            <p className="text-2xl font-bold text-gray-400">...</p>
                        </div>
                    </div>
                    <div className="mt-auto pt-4">
                        <div className="pt-3 border-t border-gray-200">
                            <p className="text-sm text-gray-500">Loading...</p>
                        </div>
                    </div>
                </div>
            );
        }

        const { clearToDrive = 0, notClearToDrive = 0, activeDrivers } = stats;
        const total = clearToDrive + notClearToDrive;

        if (activeDrivers === 0 || total === 0) {
            return (
                <div className="bg-white rounded-lg shadow p-6 border flex flex-col h-full">
                    <div className="flex items-center">
                        <div className="p-2 bg-gray-100 rounded-md">
                            <Shield className="w-6 h-6 text-gray-400" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Clear to Drive</p>
                            <p className="text-md font-bold text-gray-600">N/A</p>
                        </div>
                    </div>
                    <div className="mt-auto pt-4">
                        <div className="pt-3 border-t border-gray-200">
                            <p className="text-sm text-gray-500">No drivers</p>
                        </div>
                    </div>
                </div>
            );
        }

        const allClear = notClearToDrive === 0;
        const colorClass = allClear ? '#69BE28' : '#EF4444';

        let subtitle = '';
        if (allClear) {
            subtitle = `All ${clearToDrive} drivers clear`;
        } else {
            subtitle = `${notClearToDrive} not clear to drive`;
        }

        return (
            <div
                className="bg-white rounded-lg shadow p-6 border flex flex-col h-full cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => navigate('/drivers?clearToDrive=false')}
            >
                <div className="flex items-center">
                    <div className="p-2 rounded-md" style={{ backgroundColor: 'rgba(105, 190, 40, 0.1)' }}>
                        <Shield className="w-6 h-6" style={{ color: colorClass }} />
                    </div>
                    <div className="ml-4">
                        <p className="text-sm font-medium text-gray-600">Clear to Drive</p>
                        <p className="text-2xl font-bold" style={{ color: colorClass }}>
                            {clearToDrive}/{total}
                        </p>
                    </div>
                </div>
                <div className="mt-auto pt-4">
                    <div className="pt-3 border-t border-gray-200">
                        <p className="text-sm text-gray-500">{subtitle}</p>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#fafbfa' }}>
            {/* Header */}
            <header className="bg-white shadow-sm border-b print:hidden">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center py-2">
                        <div className="flex items-center min-w-0 flex-1">
                            <img
                                src="/logo-horizontal-black.png"
                                alt="IntelliDrive"
                                className="h-12 sm:h-16 md:h-20 w-auto flex-shrink-0"
                            />
                            {process.env.NODE_ENV === 'development' && (
                                <span className="ml-2 sm:ml-4 px-2 sm:px-3 py-1 text-xs sm:text-sm font-medium text-white rounded-full flex-shrink-0" style={{ backgroundColor: '#69BE28' }}>
                                    Dev
                                </span>
                            )}
                        </div>
                        <div className="flex items-center space-x-2 sm:space-x-4 min-w-0">
                            <span className="text-xs sm:text-sm text-gray-700 truncate hidden sm:block">
                                Welcome, {user.firstName} {user.lastName}
                            </span>
                            <span className="text-xs sm:text-sm text-gray-700 truncate sm:hidden">
                                {user.firstName}
                            </span>
                            <span className="px-1 sm:px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded flex-shrink-0">
                                {user.role}
                            </span>
                            {user?.organisation?.automatedChecks && (
                                <>
                                    {/* Desktop pill with settings link */}
                                    <span className={`hidden sm:inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full flex-shrink-0 ${
                                        automationSettings?.autoCheckOnConsent
                                            ? 'bg-green-100 text-green-700'
                                            : 'bg-gray-100 text-gray-500'
                                    }`}>
                                        <Bot className="w-3 h-3" />
                                        Auto-check on consent: {automationSettings?.autoCheckOnConsent ? 'on' : 'off'}
                                        <button
                                            onClick={() => navigate('/settings?tab=automation#consent-check')}
                                            title="Configure consent-triggered checks in Automation Settings"
                                            className="ml-0.5 hover:opacity-60 transition-opacity"
                                        >
                                            <Settings className="w-3 h-3" />
                                        </button>
                                    </span>

                                    {/* Mobile icon with tap tooltip */}
                                    <div className="relative sm:hidden flex-shrink-0">
                                        <button
                                            onClick={() => setShowAutoCheckTooltip(v => !v)}
                                            className={`p-1 rounded-full ${
                                                automationSettings?.autoCheckOnConsent
                                                    ? 'text-green-600'
                                                    : 'text-gray-400'
                                            }`}
                                        >
                                            <Bot className="w-4 h-4" />
                                        </button>
                                        {showAutoCheckTooltip && (
                                            <div className="absolute right-0 top-full mt-1 w-52 bg-white border border-gray-200 rounded-lg shadow-lg p-3 z-50">
                                                <p className="text-xs font-semibold text-gray-900 mb-1">
                                                    Auto-check on consent: {automationSettings?.autoCheckOnConsent ? 'on' : 'off'}
                                                </p>
                                                <p className="text-xs text-gray-600 mb-2">
                                                    {automationSettings?.autoCheckOnConsent
                                                        ? 'Drivers are automatically checked when they approve their consent form.'
                                                        : 'Drivers are not automatically checked when they approve their consent form.'
                                                    }
                                                </p>
                                                <button
                                                    onClick={() => { navigate('/settings?tab=automation#consent-check'); setShowAutoCheckTooltip(false); }}
                                                    className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 transition-colors"
                                                >
                                                    <Settings className="w-3 h-3" />
                                                    Configure in Automation Settings
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                            <NotificationBell
                                className="flex-shrink-0"
                                onConsentResultsClick={(data) => {
                                    setConsentResultsData(data);
                                    setShowConsentResultsModal(true);
                                }}
                            />
                            <button
                                onClick={handleLogout}
                                className="flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm text-gray-600 hover:text-gray-900 transition-colors flex-shrink-0"
                            >
                                <LogOut className="w-3 h-3 sm:w-4 sm:h-4" />
                                <span className="hidden sm:inline">Logout</span>
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Navigation */}
            <nav className="print:hidden" style={{ backgroundColor: '#231f20' }}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Desktop Navigation - Hidden until sufficient space */}
                    <div className="hidden xl:flex justify-between space-x-6 py-3">
                        {navItems.map((item) => {
                            const Icon = item.icon;
                            const isActive = location.pathname.startsWith(item.path);
                            return (
                                <button
                                    key={item.id}
                                    onClick={() => navigate(item.path)}
                                    className={`flex items-center px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-all duration-200 ${isActive
                                        ? 'text-white'
                                        : 'text-gray-300 hover:text-white'
                                        }`}
                                    style={isActive
                                        ? { backgroundColor: '#69BE28' }
                                        : undefined
                                    }
                                    onMouseEnter={(e) => {
                                        if (!isActive) {
                                            e.currentTarget.style.backgroundColor = 'rgba(105, 190, 40, 0.1)';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!isActive) {
                                            e.currentTarget.style.backgroundColor = 'transparent';
                                        }
                                    }}
                                >
                                    <Icon className="w-4 h-4 mr-2" />
                                    {item.label}
                                </button>
                            );
                        })}
                    </div>

                    {/* Mobile Navigation - Show on mobile, tablets, and smaller desktops */}
                    <div className="xl:hidden">
                        {/* Mobile Header with Burger Button */}
                        <div className="flex items-center justify-between py-3">
                            <span className="text-white text-sm font-medium">
                                {navItems.find(item => location.pathname.startsWith(item.path))?.label || 'Dashboard'}
                            </span>
                            <button
                                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                                className="text-gray-300 hover:text-white p-2"
                                aria-label="Toggle navigation menu"
                            >
                                {isMobileMenuOpen ? (
                                    <XCircle className="w-6 h-6" />
                                ) : (
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                    </svg>
                                )}
                            </button>
                        </div>

                        {/* Mobile Menu Dropdown */}
                        {isMobileMenuOpen && (
                            <div className="pb-3 space-y-1">
                                {navItems.map((item) => {
                                    const Icon = item.icon;
                                    const isActive = location.pathname.startsWith(item.path);
                                    return (
                                        <button
                                            key={item.id}
                                            onClick={() => {
                                                navigate(item.path);
                                                setIsMobileMenuOpen(false);
                                            }}
                                            className={`w-full flex items-center px-3 py-3 rounded-md text-sm font-medium transition-all duration-200 ${isActive
                                                ? 'text-white'
                                                : 'text-gray-300 hover:text-white'
                                                }`}
                                            style={isActive
                                                ? { backgroundColor: '#69BE28' }
                                                : undefined
                                            }
                                        >
                                            <Icon className="w-4 h-4 mr-3" />
                                            {item.label}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </nav>

            {/* Main Content */}
            <main className="flex-1">
                <Routes>
                    {/* Dashboard Route */}
                    <Route
                        path="/dashboard"
                        element={
                            <div className="max-w-7xl mx-auto py-3 sm:py-6 sm:px-6 lg:px-8">
                                <div className="px-4 py-3 sm:py-6 sm:px-0">

                                    {/* Print Header */}
                                    <div className="hidden print:block border-b-2 pb-4 mb-8" style={{ borderColor: '#231f20' }}>
                                        <div className="flex items-center justify-between">
                                            <img
                                                src="/logo-horizontal-black.png"
                                                alt="IntelliDrive"
                                                className="h-20 w-auto"
                                            />
                                            <div className="text-right">
                                                <h1 className="text-lg font-bold text-gray-900">
                                                    IntelliDrive Compliance Dashboard
                                                </h1>
                                                <p className="text-sm text-gray-600">
                                                    Generated: {new Date().toLocaleDateString('en-GB')}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Data Stale/Error Warning - only show when there's an issue */}
                                    {(() => {
                                        const minutesSinceRefresh = (Date.now() - lastRefresh.getTime()) / 1000 / 60;
                                        const isStale = minutesSinceRefresh > 5;
                                        const hasIssue = isStale || (error && stats);

                                        if (!hasIssue) return null;

                                        return (
                                            <div className={`mb-4 flex items-center justify-end text-xs ${error ? 'text-red-600' : 'text-amber-600'}`}>
                                                <AlertTriangle className="w-3.5 h-3.5 mr-1.5" />
                                                <span className="mr-2">
                                                    {error ? 'Refresh failed' : `Data is ${Math.floor(minutesSinceRefresh)} min old`}
                                                </span>
                                                <button
                                                    onClick={loadDashboardData}
                                                    disabled={isLoading}
                                                    className={`px-2 py-1 rounded text-xs font-medium disabled:opacity-50 ${
                                                        error
                                                            ? 'bg-red-100 hover:bg-red-200 text-red-700'
                                                            : 'bg-amber-100 hover:bg-amber-200 text-amber-700'
                                                    }`}
                                                >
                                                    {isLoading ? (
                                                        <RefreshCw className="w-3 h-3 animate-spin" />
                                                    ) : (
                                                        'Refresh'
                                                    )}
                                                </button>
                                            </div>
                                        );
                                    })()}

                                    {/* Loading State */}
                                    {isLoading && !stats && (
                                        <div className="flex items-center justify-center h-64">
                                            <div className="flex items-center space-x-2">
                                                <RefreshCw className="w-5 h-5 animate-spin" style={{ color: '#69BE28' }} />
                                                <span className="text-gray-600">Loading dashboard data...</span>
                                            </div>
                                        </div>
                                    )}

                                    {/* Error State */}
                                    {error && !stats && (
                                        <div className="flex items-center justify-center h-64">
                                            <div className="text-center">
                                                <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                                                <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Dashboard</h3>
                                                <p className="text-gray-600 mb-4">{error}</p>
                                                <button
                                                    onClick={loadDashboardData}
                                                    className="px-4 py-2 text-white rounded-md hover:opacity-90 transition-opacity"
                                                    style={{ backgroundColor: '#69BE28' }}
                                                >
                                                    Try Again
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Dashboard Content */}
                                    {stats && (
                                        <>
                                            {/* Overview Stats */}
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 md:gap-6 mb-6 md:mb-8">
                                                {/* Total Drivers */}
                                                <div
                                                    className="bg-white rounded-lg shadow p-6 border flex flex-col h-full cursor-pointer hover:shadow-md transition-shadow"
                                                    onClick={() => navigate('/drivers')}
                                                >
                                                    <div className="flex items-center">
                                                        <div className="p-2 rounded-md" style={{ backgroundColor: 'rgba(105, 190, 40, 0.1)' }}>
                                                            <Users className="w-6 h-6" style={{ color: '#69BE28' }} />
                                                        </div>
                                                        <div className="ml-4">
                                                            <p className="text-sm font-medium text-gray-600">Total Drivers</p>
                                                            <p className="text-2xl font-bold text-gray-900">{stats.totalDrivers}</p>
                                                        </div>
                                                    </div>
                                                    <div className="mt-auto pt-4">
                                                        <div className="pt-3 border-t border-gray-200">
                                                            <p className="text-sm text-gray-500">0 new this month</p>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Consent Rate */}
                                                <ConsentRateCard />

                                                {/* Clear to Drive */}
                                                <ClearToDriveCard />

                                                {/* High Risk Drivers */}
                                                <div
                                                    className="bg-white rounded-lg shadow p-6 border flex flex-col h-full cursor-pointer hover:shadow-md transition-shadow"
                                                    onClick={() => navigate('/drivers?risk=high')}
                                                >
                                                    <div className="flex items-center">
                                                        <div className="p-2 bg-red-100 rounded-md">
                                                            <AlertTriangle className="w-6 h-6 text-red-600" />
                                                        </div>
                                                        <div className="ml-4">
                                                            <p className="text-sm font-medium text-gray-600">High Risk</p>
                                                            <p className="text-2xl font-bold text-gray-900">{stats.riskLevels.high}</p>
                                                        </div>
                                                    </div>
                                                    <div className="mt-auto pt-4">
                                                        <div className="pt-3 border-t border-gray-200">
                                                            <p className="text-sm text-gray-500">Immediate attention</p>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Checks Today/This Month */}
                                                <div
                                                    className="bg-white rounded-lg shadow p-6 border flex flex-col h-full cursor-pointer hover:shadow-md transition-shadow"
                                                    onClick={() => navigate('/drivers?checkedToday=true')}
                                                >
                                                    <div className="flex items-center">
                                                        <div className="p-2 rounded-md" style={{ backgroundColor: 'rgba(35, 31, 32, 0.1)' }}>
                                                            <Shield className="w-6 h-6" style={{ color: '#231f20' }} />
                                                        </div>
                                                        <div className="ml-4">
                                                            <p className="text-sm font-medium text-gray-600">Checks Today</p>
                                                            <p className="text-2xl font-bold text-gray-900">{stats?.checksToday}</p>
                                                        </div>
                                                    </div>
                                                    <div className="mt-auto pt-4">
                                                        <div className="pt-3 border-t border-gray-200">
                                                            <p className="text-sm text-gray-500">{stats?.checksThisMonth} this month</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Main Content Grid */}
                                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                                                {/* Risk Distribution Chart */}
                                                <div className="lg:col-span-1">
                                                    <div className="bg-white rounded-lg shadow p-6 h-full flex flex-col">
                                                        <div className="flex items-center justify-between mb-4">
                                                            <h3 className="text-lg font-medium text-gray-900">Risk Distribution</h3>
                                                            <BarChart3 className="w-5 h-5 text-gray-400" />
                                                        </div>

                                                        <div className="space-y-4 flex-1">
                                                            {/* Low Risk */}
                                                            <button
                                                                onClick={() => navigate('/drivers?risk=low')}
                                                                className="w-full text-left group hover:bg-green-50 rounded-lg p-2 -m-2 transition-colors"
                                                            >
                                                                <div className="flex justify-between items-center mb-1">
                                                                    <span className="text-sm font-medium text-gray-700 group-hover:text-green-700">Low Risk</span>
                                                                    <span className="text-sm text-gray-500 group-hover:text-green-600">{stats.riskLevels.low}</span>
                                                                </div>
                                                                <div className="w-full bg-gray-200 rounded-full h-2">
                                                                    <div
                                                                        className="h-2 rounded-full"
                                                                        style={{
                                                                            width: stats.totalDrivers > 0 ? `${(stats.riskLevels.low / stats.totalDrivers) * 100}%` : '0%',
                                                                            backgroundColor: stats.riskLevels.low > 0 ? '#69BE28' : '#d1d5db'
                                                                        }}
                                                                    ></div>
                                                                </div>
                                                            </button>

                                                            {/* Medium Risk */}
                                                            <button
                                                                onClick={() => navigate('/drivers?risk=medium')}
                                                                className="w-full text-left group hover:bg-yellow-50 rounded-lg p-2 -m-2 transition-colors"
                                                            >
                                                                <div className="flex justify-between items-center mb-1">
                                                                    <span className="text-sm font-medium text-gray-700 group-hover:text-yellow-700">Medium Risk</span>
                                                                    <span className="text-sm text-gray-500 group-hover:text-yellow-600">{stats.riskLevels.medium}</span>
                                                                </div>
                                                                <div className="w-full bg-gray-200 rounded-full h-2">
                                                                    <div
                                                                        className="h-2 rounded-full"
                                                                        style={{
                                                                            width: stats.totalDrivers > 0 ? `${(stats.riskLevels.medium / stats.totalDrivers) * 100}%` : '0%',
                                                                            backgroundColor: stats.riskLevels.medium > 0 ? '#f59e0b' : '#d1d5db'
                                                                        }}
                                                                    ></div>
                                                                </div>
                                                            </button>

                                                            {/* High Risk */}
                                                            <button
                                                                onClick={() => navigate('/drivers?risk=high')}
                                                                className="w-full text-left group hover:bg-red-50 rounded-lg p-2 -m-2 transition-colors"
                                                            >
                                                                <div className="flex justify-between items-center mb-1">
                                                                    <span className="text-sm font-medium text-gray-700 group-hover:text-red-700">High Risk</span>
                                                                    <span className="text-sm text-gray-500 group-hover:text-red-600">{stats.riskLevels.high}</span>
                                                                </div>
                                                                <div className="w-full bg-gray-200 rounded-full h-2">
                                                                    <div
                                                                        className="h-2 rounded-full"
                                                                        style={{
                                                                            width: stats.totalDrivers > 0 ? `${(stats.riskLevels.high / stats.totalDrivers) * 100}%` : '0%',
                                                                            backgroundColor: stats.riskLevels.high > 0 ? '#ef4444' : '#d1d5db'
                                                                        }}
                                                                    ></div>
                                                                </div>
                                                            </button>
                                                        </div>

                                                        <div className="mt-auto pt-4 border-t">
                                                            <div className="flex items-center justify-between">
                                                                <span className="text-sm text-gray-600">Average Risk Score</span>
                                                                <span className="text-lg font-semibold text-gray-900">{stats.averageRiskScore}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Drivers Due for Checks */}
                                                <div className="lg:col-span-2">
                                                    <div className="bg-white rounded-lg shadow p-6 h-full">
                                                        <div className="flex items-center justify-between mb-4">
                                                            <div className="flex items-center">
                                                                <h3 className="text-lg font-medium text-gray-900">Drivers Due for Checks</h3>
                                                                <div className="relative ml-2 group">
                                                                    <Info className="w-4 h-4 text-gray-400 cursor-pointer" />
                                                                    <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-64 bg-gray-800 text-white text-xs rounded-lg p-3 hidden group-hover:block z-50 shadow-lg">
                                                                        <p className="font-semibold mb-1">A driver appears here if they meet all of:</p>
                                                                        <ul className="space-y-1 list-disc list-inside text-gray-300">
                                                                            <li>Active (not deactivated)</li>
                                                                            <li>Consent given and not expired</li>
                                                                            <li>Never been checked, or next check date has passed</li>
                                                                        </ul>
                                                                        <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-x-4 border-x-transparent border-t-4 border-t-gray-800" />
                                                                    </div>
                                                                </div>
                                                                {driversDue.length > 0 && (
                                                                    <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-800 rounded-full">
                                                                        {driversDue.length}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {driversDue.length > 0 && (
                                                                <div className="flex items-center space-x-2">
                                                                    <button
                                                                        onClick={() => navigate('/drivers?filter=due')}
                                                                        className="px-2.5 py-1 text-xs font-medium border border-gray-300 text-gray-600 rounded hover:bg-gray-50 transition-colors"
                                                                    >
                                                                        View All
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </div>

                                                        {driversDue.length === 0 ? (
                                                            <div className="text-center py-8">
                                                                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                                                                <h4 className="text-lg font-medium text-gray-900 mb-2">All Up to Date!</h4>
                                                                <p className="text-gray-500">No drivers require immediate licence checks.</p>
                                                            </div>
                                                        ) : (
                                                            <>

                                                                {/* Table - Top 5 Most Overdue */}
                                                                <div className="overflow-x-auto">
                                                                    <table className="min-w-full divide-y divide-gray-200">
                                                                        <thead className="bg-gray-50">
                                                                            <tr>
                                                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                                                                    Driver
                                                                                </th>
                                                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                                                                    Risk Level
                                                                                </th>
                                                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                                                                    Days Overdue
                                                                                </th>
                                                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                                                                    Reason
                                                                                </th>
                                                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                                                                    Action
                                                                                </th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody className="bg-white divide-y divide-gray-200">
                                                                            {[...driversDue]
                                                                                .sort((a, b) => b.daysOverdue - a.daysOverdue)
                                                                                .slice(0, 5)
                                                                                .map((driver) => (
                                                                                    <tr key={driver.id} className="hover:bg-gray-50">
                                                                                        <td className="px-4 py-3">
                                                                                            <div>
                                                                                                <p className="text-sm font-medium text-gray-900">
                                                                                                    {driver.firstName} {driver.lastName}
                                                                                                </p>
                                                                                                <p className="text-sm text-gray-500">{driver.licenceNumber}</p>
                                                                                            </div>
                                                                                        </td>
                                                                                        <td className="px-4 py-3">
                                                                                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRiskBadgeColor(driver.riskLevel)}`}>
                                                                                                {driver.riskLevel ? driver.riskLevel.toUpperCase() : 'UNKNOWN'}
                                                                                            </span>
                                                                                        </td>
                                                                                        <td className="px-4 py-3">
                                                                                            <span className="text-sm text-red-600 font-medium">
                                                                                                {driver.daysOverdue} days
                                                                                            </span>
                                                                                        </td>
                                                                                        <td className="px-4 py-3">
                                                                                            <span className="text-sm text-gray-900">{driver.reason}</span>
                                                                                        </td>
                                                                                        <td className="px-4 py-3">
                                                                                            <button
                                                                                                onClick={() => navigate('/drivers')}
                                                                                                className="text-sm font-medium hover:opacity-80 transition-opacity"
                                                                                                style={{ color: '#69BE28' }}
                                                                                            >
                                                                                                Check Now
                                                                                            </button>
                                                                                        </td>
                                                                                    </tr>
                                                                                ))}
                                                                        </tbody>
                                                                    </table>
                                                                </div>
                                                                {driversDue.length > 5 && (
                                                                    <div className="mt-3 text-center">
                                                                        <button
                                                                            onClick={() => navigate('/drivers?filter=due')}
                                                                            className="text-sm font-medium hover:underline transition-colors"
                                                                            style={{ color: '#69BE28' }}
                                                                        >
                                                                            View {driversDue.length - 5} more drivers →
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Site Risk Overview Widget */}
                                            <div className="mt-8">
                                                <SiteRiskWidget
                                                    onViewAll={() => navigate('/sites')}
                                                />
                                            </div>

                                            {/* Upcoming Automated Checks Widget - Only shown if organisation has the feature */}
                                            {user?.organisation?.automatedChecks && (
                                                <div className="mt-8">
                                                    <div className="bg-white rounded-lg shadow p-6 border">
                                                        <div className="flex items-center justify-between">
                                                            <div className="flex items-center space-x-3">
                                                                <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(105, 190, 40, 0.1)' }}>
                                                                    <Bot className="w-5 h-5" style={{ color: '#69BE28' }} />
                                                                </div>
                                                                <div>
                                                                    <h3 className="text-lg font-medium text-gray-900">Automated Licence Checking</h3>
                                                                    <p className="text-sm text-gray-600">Scheduled automated checks for your drivers</p>
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center space-x-2">
                                                                <button
                                                                    onClick={() => navigate('/settings?tab=automation')}
                                                                    className="px-3 py-2 text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium flex items-center space-x-2"
                                                                >
                                                                    <Settings className="w-4 h-4" />
                                                                    <span>Configure</span>
                                                                </button>
                                                                <button
                                                                    onClick={() => setIsAutomationExpanded(!isAutomationExpanded)}
                                                                    className="p-2 text-gray-400 hover:text-gray-600 rounded-md transition-colors"
                                                                    title={isAutomationExpanded ? 'Collapse' : 'Expand'}
                                                                >
                                                                    {isAutomationExpanded ? (
                                                                        <ChevronUp className="w-5 h-5" />
                                                                    ) : (
                                                                        <ChevronDown className="w-5 h-5" />
                                                                    )}
                                                                </button>
                                                            </div>
                                                        </div>

                                                        {isAutomationExpanded && (
                                                            <>
                                                                {/* Auto-Check on Consent Indicator */}
                                                                {automationSettings?.autoCheckOnConsent && (
                                                                    <div className="mt-4 mb-4 bg-green-50 border border-green-200 rounded-lg p-3">
                                                                        <div className="flex items-start">
                                                                            <CheckCircle className="w-4 h-4 mt-0.5 mr-2 flex-shrink-0" style={{ color: '#69BE28' }} />
                                                                            <div className="flex-1">
                                                                                <p className="text-xs text-green-800">
                                                                                    <strong>Automatic Licence Checks Enabled:</strong> Drivers will be automatically checked when they approve their consent form.
                                                                                </p>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                )}

                                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                                                                    <div className="bg-gray-50 rounded-lg p-4 border">
                                                                <div className="flex items-center justify-between">
                                                                    <div>
                                                                        <p className="text-sm text-gray-600">Status</p>
                                                                        <p className="text-lg font-semibold text-gray-900 mt-1">
                                                                            {!automationSettings ? (
                                                                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                                                                    <Clock className="w-3 h-3 mr-1" />
                                                                                    Loading...
                                                                                </span>
                                                                            ) : automationSettings.enabled ? (
                                                                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                                                    <CheckCircle className="w-3 h-3 mr-1" />
                                                                                    Active
                                                                                </span>
                                                                            ) : (
                                                                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                                                                    <XCircle className="w-3 h-3 mr-1" />
                                                                                    Inactive
                                                                                </span>
                                                                            )}
                                                                        </p>
                                                                    </div>
                                                                    <RefreshCw className="w-8 h-8 text-gray-300" />
                                                                </div>
                                                            </div>

                                                                    <div className="bg-gray-50 rounded-lg p-4 border">
                                                                        <div className="flex items-center justify-between">
                                                                            <div>
                                                                                <p className="text-sm text-gray-600">Next Run</p>
                                                                                <p className="text-sm font-medium text-gray-900 mt-1">
                                                                                    {automationSettings?.enabled && automationSettings?.nextScheduledRun ? (
                                                                                        new Date(automationSettings.nextScheduledRun).toLocaleString('en-GB', {
                                                                                            day: 'numeric',
                                                                                            month: 'short',
                                                                                            hour: '2-digit',
                                                                                            minute: '2-digit'
                                                                                        })
                                                                                    ) : automationSettings?.enabled ? (
                                                                                        `Today at ${automationSettings?.preferredCheckTime || 'midnight'}`
                                                                                    ) : (
                                                                                        'Not scheduled'
                                                                                    )}
                                                                                </p>
                                                                            </div>
                                                                            <Clock className="w-8 h-8 text-gray-300" />
                                                                </div>
                                                            </div>

                                                                    <div className="bg-gray-50 rounded-lg p-4 border">
                                                                        <div className="flex items-center justify-between">
                                                                            <div>
                                                                                <p className="text-sm text-gray-600">Last Run</p>
                                                                                <p className="text-lg font-semibold mt-1" style={{ color: '#69BE28' }}>
                                                                                    {automationSettings?.lastRunDriverCount !== null && automationSettings?.lastRunDriverCount !== undefined ? (
                                                                                        `${automationSettings.lastRunDriverCount} driver${automationSettings.lastRunDriverCount !== 1 ? 's' : ''}`
                                                                                    ) : (
                                                                                        'No runs yet'
                                                                                    )}
                                                                                </p>
                                                                            </div>
                                                                            <Activity className="w-8 h-8 text-gray-300" />
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                <div className="mt-4 flex items-center justify-center">
                                                                    <p className="text-xs text-gray-500">
                                                                        {automationSettings?.enabled ? (
                                                                            `Automated checks run daily at ${automationSettings?.preferredCheckTime || '03:00'} based on risk levels and check frequencies`
                                                                        ) : (
                                                                            'Enable automation in settings to start scheduled checks'
                                                                        )}
                                                                    </p>
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Recent Activity */}
                                            <div className="mt-8">
                                                <div className="bg-white rounded-lg shadow p-6">
                                                    <div className="flex items-center justify-between mb-4">
                                                        <h3 className="text-lg font-medium text-gray-900">Recent Licence Checks</h3>
                                                        <div className="flex items-center space-x-4">
                                                            <Activity className="w-5 h-5 text-gray-400" />
                                                            <button
                                                                onClick={() => navigate('/drivers')}
                                                                className="text-sm font-medium hover:opacity-80 transition-opacity"
                                                                style={{ color: '#69BE28' }}
                                                            >
                                                                View All Drivers
                                                            </button>
                                                        </div>
                                                    </div>

                                                    <div className="overflow-x-auto">
                                                        <table className="min-w-full divide-y divide-gray-200">
                                                            <thead className="bg-gray-50">
                                                                <tr>
                                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                                                        Driver
                                                                    </th>
                                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                                                        Check Date
                                                                    </th>
                                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                                                        Risk Assessment
                                                                    </th>
                                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                                                        Status
                                                                    </th>
                                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                                                        Checked By
                                                                    </th>
                                                                </tr>
                                                            </thead>
                                                            <tbody className="bg-white divide-y divide-gray-200">
                                                                {recentActivity.map((activity) => (
                                                                    <tr key={activity.id} className="hover:bg-gray-50">
                                                                        <td className="px-4 py-3">
                                                                            <div>
                                                                                <p className="text-sm font-medium text-gray-900">{activity.driverName}</p>
                                                                                <p className="text-sm text-gray-500">{activity.licenceNumber}</p>
                                                                            </div>
                                                                        </td>
                                                                        <td className="px-4 py-3">
                                                                            <div>
                                                                                <p className="text-sm text-gray-900">{formatDate(activity.checkDate)}</p>
                                                                                <p className="text-sm text-gray-500">{formatTime(activity.checkDate)}</p>
                                                                            </div>
                                                                        </td>
                                                                        <td className="px-4 py-3">
                                                                            <div className="flex items-center space-x-2">
                                                                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRiskBadgeColor(activity.riskLevel)}`}>
                                                                                    {activity.riskLevel ? activity.riskLevel.toUpperCase() : 'UNKNOWN'}
                                                                                </span>
                                                                                <span className="text-sm text-gray-500">({activity.riskScore})</span>
                                                                            </div>
                                                                        </td>
                                                                        <td className="px-4 py-3">
                                                                            <button
                                                                                onClick={() => handleViewDetails(activity)}
                                                                                className="text-sm text-gray-900 hover:text-blue-600 hover:underline transition-colors text-left"
                                                                                title="View licence check details"
                                                                            >
                                                                                {activity.status}
                                                                            </button>
                                                                        </td>
                                                                        <td className="px-4 py-3">
                                                                            <div className="flex items-center space-x-2">
                                                                                <span className="text-sm text-gray-500">{activity.checkedBy}</span>
                                                                                {activity.isAutomated && (
                                                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                                                                                        <Bot className="w-3 h-3 mr-1" />
                                                                                        AUTO
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            </div>
                                        </>
                                    )}

                                    {/* Fallback for when no data and no loading/error */}
                                    {!stats && !isLoading && !error && (
                                        <div className="border-4 border-dashed border-gray-200 rounded-lg h-96 flex items-center justify-center">
                                            <div className="text-center">
                                                <h3 className="text-lg font-medium mb-2" style={{ color: '#231f20' }}>
                                                    Welcome to IntelliDrive!
                                                </h3>
                                                <p className="text-gray-600 mb-4">
                                                    Your intelligent compliance platform is ready.
                                                </p>
                                                <button
                                                    onClick={loadDashboardData}
                                                    className="inline-flex items-center px-4 py-2 text-white text-sm font-medium rounded-md hover:opacity-90 transition-opacity mr-4"
                                                    style={{ backgroundColor: '#69BE28' }}
                                                >
                                                    <BarChart3 className="w-4 h-4 mr-2" />
                                                    Load Smart Dashboard
                                                </button>
                                                <button
                                                    onClick={() => navigate('/drivers')}
                                                    className="inline-flex items-center px-4 py-2 text-white text-sm font-medium rounded-md hover:opacity-90 transition-opacity"
                                                    style={{ backgroundColor: '#69BE28' }}
                                                >
                                                    <Users className="w-4 h-4 mr-2" />
                                                    Manage Drivers
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        }
                    />

                    {/* Site Risk Full Page */}
                    <Route path="/sites" element={
                        <SiteRiskPage />
                    } />

                    {/* Drivers Route */}
                    <Route path="/drivers" element={<Drivers user={user} />} />

                    {/* Reports Route */}
                    <Route path="/reports" element={<Reports user={user} />} />

                    <Route path="/bulk-operations" element={<Navigate to="/drivers" replace />} />

                    <Route path="/user-management" element={<UserManagement user={user} />} />

                    <Route path="/settings" element={
                        <div className="max-w-7xl mx-auto py-3 sm:py-6 sm:px-6 lg:px-8">
                            <div className="px-4 py-3 sm:py-6 sm:px-0">
                                <SettingsTabs userRole={user?.role || 'user'} user={user} onAutomationSettingsChange={setAutomationSettings} />
                            </div>
                        </div>
                    } />

                    {/* Default redirect */}
                    <Route path="/" element={<Navigate to="/dashboard" replace />} />

                    <Route path="*" element={<NotFoundPage />} />
                </Routes>
            </main>

            {/* Footer */}
            <Footer />

            {/* Licence Check Modal */}
            {selectedDriverForModal && (
                <LicenceCheckModal
                    isOpen={isLicenceCheckModalOpen}
                    onClose={() => {
                        setIsLicenceCheckModalOpen(false);
                        setSelectedDriverForModal(null);
                    }}
                    driver={selectedDriverForModal}
                    onCheckComplete={loadDashboardData}
                    subtype={user?.organisation?.subtype}
                />
            )}

            {/* Bulk Operation Modal */}
            {isBulkOperationModalOpen && bulkOperationId && (
                <BulkOperationModal
                    bulkOperationId={bulkOperationId}
                    onClose={() => {
                        setIsBulkOperationModalOpen(false);
                        setBulkOperationId(null);
                    }}
                />
            )}

            {/* Consent Send Results Modal */}
            <ConsentSendResultsModal
                isOpen={showConsentResultsModal}
                onClose={() => {
                    setShowConsentResultsModal(false);
                    setConsentResultsData(null);
                }}
                emailsSent={consentResultsData?.emailsSent || 0}
                totalImported={consentResultsData?.totalDrivers || 0}
                driversWithoutEmail={consentResultsData?.driversWithoutEmail || []}
            />
        </div>
    );
};

// Settings Tabs Component
interface SettingsTabsProps {
    userRole: string;
    user: User | null;
    onAutomationSettingsChange?: (settings: any) => void;
}

const SettingsTabs: React.FC<SettingsTabsProps> = ({ userRole, user, onAutomationSettingsChange }) => {
    const [searchParams] = useSearchParams();
    const tabFromUrl = searchParams.get('tab');
    const [activeTab, setActiveTab] = useState(tabFromUrl || 'health');

    // Update active tab when URL parameter changes
    useEffect(() => {
        if (tabFromUrl) {
            setActiveTab(tabFromUrl);
        }
    }, [tabFromUrl]);

    // Build tabs array - conditionally include automation if organisation has the feature
    const tabs = [
        { id: 'health', label: 'System Health', icon: Activity },
        { id: 'email', label: 'Email Analytics', icon: Mail },
        { id: 'notifications', label: 'Notifications', icon: Bell },
        { id: 'organisation', label: 'Organisation', icon: Building2 },
    ];

    // Add automation tab only if the organisation has paid for it
    if (user?.organisation?.automatedChecks) {
        tabs.push({ id: 'automation', label: 'Automated Checks', icon: Bot });
    }

    return (
        <div className="space-y-6">
            {/* Tab Navigation */}
            <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8">
                    {tabs.map((tab) => {
                        const Icon = tab.icon;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm ${activeTab === tab.id
                                    ? 'text-gray-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                <Icon className={`mr-2 h-5 w-5 ${activeTab === tab.id ? 'text-gray-600' : 'text-gray-400 group-hover:text-gray-500'
                                    }`} />
                                {tab.label}
                            </button>
                        );
                    })}
                </nav>
            </div>

            {/* Tab Content */}
            <div>
                {activeTab === 'health' && <SystemHealth userRole={userRole} />}
                {activeTab === 'email' && <EmailAnalytics userRole={userRole} />}
                {activeTab === 'notifications' && <NotificationsTab />}
                {activeTab === 'organisation' && <OrganisationBranding user={user} />}
                {activeTab === 'automation' && user?.organisation?.automatedChecks && (
                    <AutomationSettings user={user} onSettingsChange={onAutomationSettingsChange} />
                )}
            </div>
        </div>
    );
};

// Notifications Tab Component (wrapper for Settings page)
const NotificationsTab: React.FC = () => {
    const [activeSubTab, setActiveSubTab] = useState('settings');

    const subTabs = [
        { id: 'settings', label: 'Notification Settings' },
        { id: 'logs', label: 'Notification History' },
    ];

    return (
        <div className="space-y-6">
            {/* Sub-tab Navigation */}
            <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8">
                    {subTabs.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveSubTab(tab.id)}
                            className={`py-4 px-1 border-b-2 font-medium text-sm ${activeSubTab === tab.id
                                ? 'text-gray-900'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                            style={activeSubTab === tab.id ? { borderColor: '#69BE28' } : { borderColor: 'transparent' }}
                        >
                            {tab.label}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Sub-tab Content */}
            <div>
                {activeSubTab === 'settings' && <NotificationSettings user={null} />}
                {activeSubTab === 'logs' && <NotificationLogs user={null} />}
            </div>
        </div>
    );
};

export default Dashboard;